import { GameWithDate } from '../types/types';
import { dateToDateString } from './dates';

// src/utils/api.ts
const API_URL = process.env.REACT_APP_API_BASE_URL;
const jwtKey = 'token';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface SecurePingResponse {
  message: string;
}

interface LoginResponse {
  token: string;
}

interface PostStatisticsResponse {
  message: string;
  id: string;
}

export const api = {
  login,
  getToken,
  logout,
  securePing,
  getCurrentGame,
  getGameByDate,
  postStatisticsForDate,
  getStatisticsForDate,
  getAllGamesAdmin,
  uploadGameAdmin,
  deleteGameAdmin,
};

async function login(
  username: string,
  password: string,
): Promise<ApiResponse<LoginResponse>> {
  const res = await fetch(`${API_URL}/admin/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  if (!res.ok) {
    const data = await res.json();
    return { success: false, error: data.message };
  }

  const data = await res.json();
  localStorage.setItem(jwtKey, data.token);

  return { success: true, data: data };
}

function getToken(): string | null {
  return localStorage.getItem(jwtKey);
}

function logout() {
  localStorage.removeItem(jwtKey);
}

async function securePing(): Promise<ApiResponse<SecurePingResponse>> {
  try {
    const result = await fetchWithAuth('/admin/portal/secure-ping');
    return { success: true, data: result as SecurePingResponse };
  } catch (error) {
    console.error('Error during secure ping:', error);
    return { success: false, error: (error as Error).message };
  }
}

async function getAllGamesAdmin(): Promise<ApiResponse<GameWithDate[]>> {
  try {
    const result = await fetchWithAuth('/admin/portal/');
    return { success: true, data: result as GameWithDate[] };
  } catch (error) {
    console.error('Error during fetch all games admin:', error);
    return { success: false, error: (error as Error).message };
  }
}

async function deleteGameAdmin(id: string): Promise<ApiResponse<unknown>> {
  try {
    const result = await fetchWithAuth(`/admin/portal/${id}`, {
      method: 'DELETE',
    });
    return { success: true, data: result };
  } catch (error) {
    console.error('Error during delete game.admin:', error);
    return { success: false, error: (error as Error).message };
  }
}

async function uploadGameAdmin(
  game: Omit<GameWithDate, '_id'>,
): Promise<ApiResponse<unknown>> {
  try {
    const result = await fetchWithAuth('/admin/portal/', {
      method: 'POST',
      body: JSON.stringify(game),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return { success: true, data: result as { message: string } };
  } catch (error) {
    console.error('Error during upload game admin:', error);
    return { success: false, error: (error as Error).message };
  }
}

async function getCurrentGame(): Promise<ApiResponse<GameWithDate>> {
  const result = await fetch(`${API_URL}/game/current`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!result.ok) {
    const data = await result.json();
    return { success: false, error: data.message };
  }

  const data = await result.json();
  return { success: true, data: data };
}

async function getGameByDate(date: Date): Promise<ApiResponse<GameWithDate>> {
  const dateString = dateToDateString(date);

  const result = await fetch(`${API_URL}/game/${dateString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!result.ok) {
    const data = await result.json();
    return { success: false, error: data.message };
  }

  const data = await result.json();
  return { success: true, data: data };
}

async function postStatisticsForDate(
  date: Date,
  statistics: StatisticsRequest,
): Promise<ApiResponse<PostStatisticsResponse>> {
  const dateString = dateToDateString(date);

  const result = await fetch(`${API_URL}/gameStats/${dateString}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(statistics),
  });

  if (!result.ok) {
    const data = await result.json();
    return { success: false, error: data.message };
  }

  const data = await result.json();
  return { success: true, data: data };
}

async function getStatisticsForDate(
  date: Date,
  statistics: StatisticsRequest,
): Promise<ApiResponse<GetStatisticsResponse>> {
  const dateString = dateToDateString(date);
  const params = new URLSearchParams({
    hintsUsed: statistics.hintsUsed.toString(),
    moveCount: statistics.moveCount.toString(),
    secondsToComplete: statistics.secondsToComplete.toString(),
    streak: statistics.streak.toString(),
  }).toString();

  const result = await fetch(`${API_URL}/gameStats/${dateString}?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!result.ok) {
    const data = await result.json();
    return { success: false, error: data.message };
  }

  const data = await result.json();
  return { success: true, data: data };
}

async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
  const token = api.getToken();
  if (!token) throw new Error('No token found');

  const res = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Request failed');
  }

  return res.json();
}

export interface StatisticsRequest {
  hintsUsed: number;
  moveCount: number;
  secondsToComplete: number;
  streak: number;
}

export interface GetStatisticsResponse {
  playerCount: number;
  beatMoves: number;
  beatTime: number;
}
